import DescriptionOutlined from "@mui/icons-material/DescriptionOutlined";
import OpenInNew from "@mui/icons-material/OpenInNew";
import { Box, Link, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  documentSourceMetaLine,
  isDocumentSource,
  sortSourcesById,
  sourceHostname,
  sourceLabel,
  type CitationSource,
  type CitedDocumentSource,
  type CitedSource,
} from "@/shared/lib/citations";

type SourcesListProps = {
  sources: CitationSource[];
};

/** The line under the link: where it came from and when, from whichever of the optional
 * fields the source actually carries. Empty for a bare `{ id, url }` source — the link text
 * is already the hostname, and repeating it under itself says nothing. */
function webSourceMetaLine(source: CitedSource): string {
  const hostname = sourceHostname(source.url);
  const label = sourceLabel(source);
  return [
    source.publisher,
    // The hostname is what tells a reader whether a link is official before they click it —
    // worth showing next to a title, redundant when it is already the label or the publisher.
    hostname === source.publisher || hostname === label ? undefined : hostname,
    source.published_date ? `Published ${source.published_date}` : undefined,
    source.accessed_date ? `Accessed ${source.accessed_date}` : undefined,
  ]
    .filter(Boolean)
    .join(" · ");
}

const LIST_SX = {
  listStyle: "none",
  m: 0,
  p: 0,
  display: "flex",
  flexDirection: "column",
  gap: 1.25,
} as const;

const META_LINE_SX = {
  display: "block",
  color: "var(--theme-text-secondary)",
  wordBreak: "break-word",
} as const;

function GroupHeading({ children }: { children: React.ReactNode }) {
  return (
    <Typography
      variant="caption"
      sx={{ display: "block", fontWeight: 700, color: "var(--theme-text-secondary)", mb: 0.75 }}
    >
      {children}
    </Typography>
  );
}

/** A web source: its `[n]` number, since that is how the prose cites it, then a link out. */
function WebSourceRow({ source }: { source: CitedSource }) {
  const metaLine = webSourceMetaLine(source);
  return (
    <Box component="li" sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
      <Typography
        variant="caption"
        sx={{
          fontWeight: 700,
          color: "var(--theme-primary-light)",
          minWidth: 22,
          flexShrink: 0,
          pt: 0.25,
        }}
      >
        [{source.id}]
      </Typography>
      <Box sx={{ minWidth: 0 }}>
        <Link
          href={source.url}
          target="_blank"
          rel="noopener noreferrer"
          variant="body2"
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 0.5,
            color: "var(--theme-primary-light)",
            textDecorationColor: "var(--theme-primary-light)",
            wordBreak: "break-word",
          }}
        >
          {sourceLabel(source)}
          <OpenInNew sx={{ fontSize: 13, flexShrink: 0 }} aria-hidden />
        </Link>
        {metaLine ? (
          <Typography variant="caption" sx={META_LINE_SX}>
            {metaLine}
          </Typography>
        ) : null}
      </Box>
    </Box>
  );
}

/** A document source: no number — the prose names it by file, not by `[n]` — and no link,
 * because there is nothing to open. The document icon is what marks it as a document citation. */
function DocumentSourceRow({ source }: { source: CitedDocumentSource }) {
  const metaLine = documentSourceMetaLine(source);
  return (
    <Box component="li" sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
      <DescriptionOutlined
        sx={{ fontSize: 16, flexShrink: 0, mt: 0.25, color: "var(--theme-primary-light)" }}
        aria-hidden
      />
      <Box sx={{ minWidth: 0 }}>
        <Typography
          variant="body2"
          sx={{ color: "var(--theme-text-primary)", wordBreak: "break-word" }}
        >
          {sourceLabel(source)}
        </Typography>
        {metaLine ? (
          <Typography variant="caption" sx={META_LINE_SX}>
            {metaLine}
          </Typography>
        ) : null}
      </Box>
    </Box>
  );
}

/** The source list the inline citations point at, in two groups.
 *
 * **Document citations** come first and are listed by file name, matching how the prose names
 * them inline. Their page preview (when the passage was a slide) is shown separately — see
 * `NuggetDocumentPreviews`.
 *
 * **Web citations** keep their `[n]` numbers, ordered by citation number rather than array
 * position so `[1]` is always the first row, and link out.
 *
 * Group headings appear only when there are document citations: a list of web sources alone —
 * every chat turn today — renders exactly as it did before documents existed. */
export function SourcesList({ sources }: SourcesListProps) {
  const { t } = useTranslation("common");
  const ordered = sortSourcesById(sources);
  const documentSources = ordered.filter(isDocumentSource);
  const webSources = ordered.filter(
    (source): source is CitedSource => !isDocumentSource(source),
  );

  const webList =
    webSources.length > 0 ? (
      <Box component="ol" sx={LIST_SX}>
        {webSources.map((source) => (
          <WebSourceRow key={source.id} source={source} />
        ))}
      </Box>
    ) : null;

  if (documentSources.length === 0) {
    return webList;
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
      <Box>
        <GroupHeading>{t("citations.documentCitations")}</GroupHeading>
        <Box component="ul" sx={LIST_SX}>
          {documentSources.map((source) => (
            <DocumentSourceRow key={source.id} source={source} />
          ))}
        </Box>
      </Box>
      {webList ? (
        <Box>
          <GroupHeading>{t("citations.webCitations")}</GroupHeading>
          {webList}
        </Box>
      ) : null}
    </Box>
  );
}
